import { z } from 'zod';
import { limparCnpj, validarCnpj, validarDocumento } from './cnpj';
import { variaveisDesconhecidas } from './render';

// Todos os schemas de entrada são .strict(): campo desconhecido (inclusive tenantId) ⇒ 400. RN-ARQ-03

const senha = z
  .string()
  .min(10, 'A senha precisa ter pelo menos 10 caracteres.')
  .max(200);

const email = z.string().trim().toLowerCase().email('E-mail inválido.').max(150);

/** CPF ou CNPJ da conta: sem máscara; vazio = não informado. */
export const documentoConta = z
  .string()
  .trim()
  .transform(limparCnpj)
  .refine((v) => v === '' || ((v.length === 11 || v.length === 14) && validarDocumento(v)), 'CPF ou CNPJ inválido.')
  .optional();

export const cadastroSchema = z
  .object({
    empresa: z.string().trim().min(2).max(150),
    /** CNPJ da empresa ou CPF de quem assina para uso próprio (opcional). */
    documento: documentoConta,
    /** Vindos da consulta do CNPJ (opcionais) */
    cidade: z.string().trim().max(100).optional(),
    estado: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/).optional(),
    nome: z.string().trim().min(2).max(150),
    email,
    senha,
    aceiteTermos: z.literal(true, { errorMap: () => ({ message: 'É preciso aceitar os termos.' }) }),
  })
  .strict();

export const loginSchema = z.object({ email, senha: z.string().min(1).max(200) }).strict();

export const selecionarEmpresaSchema = z
  .object({ preAuthToken: z.string().min(10), tenantId: z.string().uuid() })
  .strict();

// Gestão de usuários (03-usuarios-rbac.md §4). MVP: admin cria o usuário com senha inicial.
export const usuarioCreateSchema = z
  .object({
    nome: z.string().trim().min(2, 'Informe o nome.').max(150),
    email,
    perfil: z.enum(['ADMIN', 'VENDEDOR']),
    senha,
  })
  .strict();

export const usuarioUpdateSchema = z.object({ status: z.enum(['ATIVO', 'INATIVO']) }).strict();

/** WhatsApp do vendedor nesta empresa (botão "Falar no WhatsApp" dos e-mails, specs/24). Vazio/null apaga. */
export const whatsappVendedorSchema = z
  .object({ whatsapp: z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(30).nullable()) })
  .strict();

export const trocarSenhaSchema = z
  .object({ senhaAtual: z.string().min(1, 'Informe a senha atual.').max(200), novaSenha: senha })
  .strict()
  .refine((d) => d.senhaAtual !== d.novaSenha, { message: 'A nova senha precisa ser diferente da atual.', path: ['novaSenha'] });

export const esqueciSenhaSchema = z.object({ email }).strict();
export const redefinirSenhaSchema = z.object({ token: z.string().min(20).max(200), novaSenha: senha }).strict();

// Retornos (tarefas de acompanhamento) — 07-agenda-tarefas.md
export const PERIODOS_RETORNO = ['atrasados', 'hoje', 'proximos', 'concluidos'] as const;
export const TIPOS_RETORNO = ['WHATSAPP', 'LIGACAO', 'VISITA', 'REUNIAO', 'EMAIL', 'OUTRO'] as const;
export const listaRetornosQuery = z
  .object({ periodo: z.enum(PERIODOS_RETORNO).default('hoje'), responsavelId: z.string().uuid().optional() })
  .strict();
export const retornoCreateSchema = z
  .object({
    clienteId: z.string().uuid(),
    tipo: z.enum(TIPOS_RETORNO).default('WHATSAPP'),
    titulo: z.string().trim().min(2, 'Título muito curto.').max(150),
    descricao: z.string().trim().max(500).optional(),
    data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida.'),
    hora: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora inválida.').default('09:00'),
    responsavelId: z.string().uuid().optional(),
  })
  .strict();
export const reagendarRetornoSchema = z
  .object({
    dias: z.number().int().min(1).max(90).optional(),
    data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida.').optional(),
  })
  .strict()
  .refine((d) => (d.dias == null) !== (d.data == null), { message: 'Informe dias ou data.' });

export const trocarEmpresaSchema = z.object({ tenantId: z.string().uuid() }).strict();

export const BASES_LEGAIS = ['CONSENTIMENTO', 'LEGITIMO_INTERESSE', 'EXECUCAO_CONTRATO'] as const;
/** Grupos fixos do sistema. Cada status cadastrável (status_cliente) pertence a um deles. */
export const STATUS_CLIENTE = ['PROSPECT', 'ATIVO', 'INATIVO'] as const;
export type GrupoStatus = (typeof STATUS_CLIENTE)[number];
export const GRUPOS_STATUS: Record<GrupoStatus, { nome: string; curto: string; descricao: string }> = {
  PROSPECT: { nome: 'Prospect', curto: 'Ainda não comprou', descricao: 'ainda não compraram — inclui quem veio da prospecção' },
  ATIVO: { nome: 'Ativo', curto: 'Carteira ativa', descricao: 'carteira ativa — contam nos indicadores e campanhas' },
  INATIVO: { nome: 'Inativo', curto: 'Fora da carteira', descricao: 'fora da carteira — não contam nos indicadores' },
};
export const CORES_STATUS = ['cinza', 'azul', 'verde', 'turquesa', 'amarelo', 'laranja', 'vermelho', 'roxo'] as const;
export type CorStatus = (typeof CORES_STATUS)[number];

export const statusClienteSchema = z
  .object({
    nome: z.string().trim().min(1).max(40),
    cor: z.enum(CORES_STATUS).default('cinza'),
    grupo: z.enum(STATUS_CLIENTE),
    /** Status inicial de cliente cadastrado à mão */
    padraoNovo: z.boolean().default(false),
    /** Status inicial de quem vem da prospecção */
    padraoProspeccao: z.boolean().default(false),
  })
  .strict();
export const statusClienteUpdateSchema = z
  .object({
    nome: z.string().trim().min(1).max(40),
    cor: z.enum(CORES_STATUS),
    grupo: z.enum(STATUS_CLIENTE),
    padraoNovo: z.boolean(),
    padraoProspeccao: z.boolean(),
  })
  .partial()
  .strict();
export const statusOrdemSchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(200) }).strict();
export const statusArquivarSchema = z.object({ moverPara: z.string().uuid().optional() }).strict();

const dataISO = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (AAAA-MM-DD).');
const opcional = <T extends z.ZodTypeAny>(s: T) =>
  z.preprocess((v) => (v === '' ? null : v), s.nullable().optional());

/** Vendedores além do principal (5 no total por cliente). */
export const MAX_VENDEDORES_ADICIONAIS = 4;

export const clienteCreateSchema = z
  .object({
    nome: z.string().trim().min(1).max(150),
    whatsapp: z.string().trim().min(8).max(30),
    empresa: opcional(z.string().trim().max(150)),
    documento: opcional(
      z
        .string()
        .transform(limparCnpj) // CPF: só dígitos; CNPJ: numérico ou alfanumérico (jul/2026)
        .refine((v) => /^[0-9]{11}$/.test(v) || validarCnpj(v), 'CPF/CNPJ inválido.'),
    ),
    email: opcional(z.string().trim().toLowerCase().email().max(150)),
    cidade: opcional(z.string().trim().max(100)),
    estado: opcional(z.string().trim().toUpperCase().length(2)),
    segmento: opcional(z.string().trim().max(100)),
    /** Grupo (legado). Sem statusId nem status, o cliente recebe o status padrão para novos. */
    status: z.enum(STATUS_CLIENTE).optional(),
    statusId: z.string().uuid().optional(),
    ultimaCompra: opcional(dataISO),
    valorUltimaCompra: opcional(z.coerce.number().min(0).max(1e12)),
    vendedorId: opcional(z.string().uuid()),
    /** Também atendem o cliente, além do principal (vendedorId). Até 4 (specs/22). Só quem atribui carteira. */
    vendedoresAdicionais: z.array(z.string().uuid()).max(MAX_VENDEDORES_ADICIONAIS, `No máximo ${MAX_VENDEDORES_ADICIONAIS} vendedores além do principal.`).optional(),
    tagIds: z.array(z.string().uuid()).max(20).default([]),
    baseLegal: z.enum(BASES_LEGAIS),
    consentimentoOrigem: z.string().trim().min(2).max(200),
    observacoes: opcional(z.string().max(5000)),
    /** Canal preferido para campanhas e contato (null = segue o padrão da empresa) */
    canalPreferido: opcional(z.enum(['WHATSAPP', 'EMAIL'])),
  })
  .strict();

export const clienteUpdateSchema = clienteCreateSchema
  .omit({ baseLegal: true, consentimentoOrigem: true })
  .partial()
  .strict();

export const naoContactarSchema = z
  .object({ motivo: z.enum(['PEDIU_NAO_RECEBER', 'NUMERO_ERRADO', 'OUTRO']), detalhe: z.string().max(300).optional() })
  .strict();

export const notaSchema = z.object({ texto: z.string().trim().min(1).max(5000) }).strict();

export const vendaContatoSchema = z
  .object({
    valor: z.coerce.number().positive('Informe o valor da venda.').max(1e12),
    /** Data da venda (AAAA-MM-DD). É ela que conta no Dashboard, na meta e nas vendas do mês. */
    data: dataISO,
    /** Atualiza clientes.ultima_compra / valor_ultima_compra (só se a venda for a mais recente). */
    atualizarUltimaCompra: z.boolean().default(true),
    /** Fecha esta oportunidade aberta do cliente como ganha, em vez de criar outra. */
    oportunidadeId: z.string().uuid().optional(),
  })
  .strict();

// ----------------------------------------------------------------------------- negociação (specs/21)
export const PEDE_VALOR = ['NAO', 'OPCIONAL', 'OBRIGATORIO'] as const;
export type PedeValor = (typeof PEDE_VALOR)[number];
export const ETAPAS_NEGOCIACAO_MAX = 12;
const textoOpcional = (max: number) => z.string().trim().max(max).nullish().transform((v) => (v ? v : null));

export const etapaNegociacaoSchema = z
  .object({
    id: z.string().uuid().optional(),
    nome: z.string().trim().min(1).max(30),
    pedeQuantidade: z.boolean().default(false),
    rotuloQuantidade: textoOpcional(40),
    pedeValor: z.enum(PEDE_VALOR).default('OPCIONAL'),
    rotuloValor: textoOpcional(40),
    prazoDias: z.number().int().min(1).max(365).nullish().transform((v) => v ?? null),
    somaPipeline: z.boolean().default(true),
  })
  .strict();

export const configNegociacaoSchema = z
  .object({
    ativa: z.boolean(),
    etapas: z.array(etapaNegociacaoSchema).min(1).max(ETAPAS_NEGOCIACAO_MAX),
    ganhoNome: z.string().trim().min(1).max(30),
    perdidoNome: z.string().trim().min(1).max(30),
    motivos: z.array(z.string().trim().min(1).max(60)).min(1).max(30),
  })
  .strict()
  .superRefine((d, ctx) => {
    const nomes = [...d.etapas.map((e) => e.nome), d.ganhoNome, d.perdidoNome].map((n) => n.toLowerCase());
    if (new Set(nomes).size !== nomes.length) ctx.addIssue({ code: 'custom', path: ['etapas'], message: 'Cada etapa precisa de um nome diferente.' });
    const m = d.motivos.map((n) => n.toLowerCase());
    if (new Set(m).size !== m.length) ctx.addIssue({ code: 'custom', path: ['motivos'], message: 'Motivo repetido.' });
  });

/** Um passo da negociação: ir para uma etapa em aberto, ganhar ou perder. */
export const passoNegociacaoSchema = z
  .object({
    destino: z.enum(['ETAPA', 'GANHO', 'PERDIDO']),
    /** Etapa em aberto (destino ETAPA) */
    etapaId: z.string().uuid().optional(),
    /** Avança esta negociação aberta do cliente; omitido = nova negociação */
    oportunidadeId: z.string().uuid().optional(),
    valor: z.coerce.number().min(0).max(1e12).optional(),
    quantidade: z.coerce.number().int().min(0).max(1e9).optional(),
    /** Prazo para cobrar (etapa em aberto). Omitido = hoje + prazo da etapa. */
    prazoEm: dataISO.optional(),
    /** Data da venda (GANHO). Omitido = hoje. */
    data: dataISO.optional(),
    motivo: z.string().trim().min(1).max(60).optional(),
    atualizarUltimaCompra: z.boolean().default(true),
    observacao: z.string().trim().max(1000).optional(),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (d.destino === 'ETAPA' && !d.etapaId) ctx.addIssue({ code: 'custom', path: ['etapaId'], message: 'Escolha a etapa.' });
    if (d.destino === 'GANHO' && !(d.valor && d.valor > 0)) ctx.addIssue({ code: 'custom', path: ['valor'], message: 'Informe o valor da venda.' });
    if (d.destino === 'PERDIDO' && !d.motivo) ctx.addIssue({ code: 'custom', path: ['motivo'], message: 'Escolha o motivo da perda.' });
  });
export type PassoNegociacao = z.infer<typeof passoNegociacaoSchema>;

export const NEGOCIACOES_PERIODOS = ['mes', 'anterior', '90d'] as const;
export const negociacoesQuery = z
  .object({ periodo: z.enum(NEGOCIACOES_PERIODOS).default('mes'), usuarioId: z.string().uuid().optional() })
  .strict();

export const contatoAvulsoSchema = z
  .object({
    canal: z.enum(['WHATSAPP', 'LIGACAO', 'EMAIL', 'PRESENCIAL']),
    /** Quando o contato aconteceu (ISO com fuso). Omitido = agora. Até 30 dias para trás, nunca no futuro. */
    realizadoEm: z.string().datetime({ offset: true, message: 'Data e hora do contato inválidas.' }).optional(),
    mensagem: z.string().max(1500).optional(),
    resultado: z.enum(['ENVIADO', 'SEM_RESPOSTA', 'INTERESSADO', 'NAO_INTERESSADO', 'RETORNAR']).optional(),
    observacao: z.string().max(1000).optional(),
    /** Novo status do cliente, gravado na mesma transação do contato (omitido = mantém). */
    statusId: z.string().uuid().optional(),
    /** Venda feita neste contato: vira uma oportunidade GANHA com fechamento na data da venda (specs/18). */
    venda: vendaContatoSchema.optional(),
    /** Passo de negociação (módulo ligado): etapa em aberto, ganho ou perdido (specs/21). */
    negociacao: passoNegociacaoSchema.optional(),
  })
  .strict()
  .refine((d) => !(d.venda && d.negociacao), { message: 'Envie venda ou negociação, não os dois.', path: ['negociacao'] });

export const whatsappLinkSchema = z.object({ mensagem: z.string().trim().min(1).max(1500) }).strict();

export const tagCreateSchema = z
  .object({
    nome: z.string().trim().min(1).max(40),
    cor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#3B82F6'),
  })
  .strict();

export const listaClientesQuery = z
  .object({
    busca: z.string().trim().max(100).optional(),
    /** Grupo inteiro (PROSPECT/ATIVO/INATIVO) */
    status: z.enum(STATUS_CLIENTE).optional(),
    /** Um status específico */
    statusId: z.string().uuid().optional(),
    tagId: z.string().uuid().optional(),
    vendedorId: z.string().uuid().optional(),
    /** Com vendedorId: inclui os clientes em que ele também atende (não só os da carteira principal). */
    comAdicionais: z.enum(['true', 'false']).optional(),
    semVendedor: z.enum(['true', 'false']).optional(),
    /** "Completar cadastro": clientes sem WhatsApp (ex.: importados da prospecção) */
    semWhatsapp: z.enum(['true', 'false']).optional(),
    /** Só clientes com e-mail válido (não descadastrado) */
    comEmail: z.enum(['true', 'false']).optional(),
    comunicacao: z.enum(['PERMITIDO', 'NAO_CONTACTAR']).optional(),
    sort: z.enum(['nome', '-nome', 'ultimaCompra', '-ultimaCompra', 'ultimoContato', '-ultimoContato', '-createdAt']).default('nome'),
    page: z.coerce.number().int().min(1).max(200).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();

/** Filtros do público da campanha — subconjunto da DSL de segmentos (05-segmentacao.md). */
export const filtrosPublicoSchema = z
  .object({
    /** Grupos inteiros; junto com statusIds vale “um OU outro” */
    status: z.array(z.enum(STATUS_CLIENTE)).max(3).optional(),
    statusIds: z.array(z.string().uuid()).max(100).optional(),
    tagIds: z.array(z.string().uuid()).max(20).optional(),
    semCompraHaDias: z.coerce.number().int().min(1).max(3650).optional(),
    incluirSemCompra: z.boolean().optional(),
    semContatoHaDias: z.coerce.number().int().min(1).max(3650).optional(),
    vendedorId: z.string().uuid().optional(),
    cidade: z.string().trim().max(100).optional(),
  })
  .strict();
export type FiltrosPublico = z.infer<typeof filtrosPublicoSchema>;

const comVariaveis = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .superRefine((v, ctx) => {
      const d = variaveisDesconhecidas(v);
      if (d.length) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Variáveis desconhecidas: ${d.join(', ')}` });
    });

/** WHATSAPP · EMAIL · PREFERENCIA (cada cliente pelo canal preferido; sem preferência, o padrão da empresa) */
export const CANAIS_CAMPANHA = ['WHATSAPP', 'EMAIL', 'PREFERENCIA'] as const;
export type CanalCampanha = (typeof CANAIS_CAMPANHA)[number];

const campanhaBase = z
  .object({
    nome: z.string().trim().min(2).max(100),
    canal: z.enum(CANAIS_CAMPANHA).default('WHATSAPP'),
    /** Sem o canal escolhido (ex.: prefere e-mail e não tem e-mail válido), usa o outro */
    canalAlternativo: z.boolean().default(true),
    /** Mensagem do WhatsApp (vazia em campanha só de e-mail) */
    mensagem: comVariaveis(1000).default(''),
    emailAssunto: opcional(comVariaveis(200)),
    emailCorpo: opcional(comVariaveis(50000)),
    emailTemplateId: opcional(z.string().uuid()),
    filtros: filtrosPublicoSchema.default({}),
    atribuicao: z.enum(['VENDEDOR_DO_CLIENTE', 'CRIADOR']).default('VENDEDOR_DO_CLIENTE'),
    dataInicio: opcional(dataISO),
    dataFim: opcional(dataISO),
    /** Template de WhatsApp de origem (a mensagem e a imagem são copiadas para a campanha) */
    templateId: opcional(z.string().uuid()),
    /** Botão "Falar no WhatsApp" no e-mail (specs/24) */
    whatsappBotao: z.boolean().default(false),
    whatsappTexto: opcional(comVariaveis(40)),
    whatsappMensagem: opcional(comVariaveis(500)),
    /** Quem clicar vai para este status (null = não muda) */
    whatsappStatusId: opcional(z.string().uuid()),
    /** ...exceto quem já está num status do grupo ATIVO */
    whatsappProtegeAtivo: z.boolean().default(true),
  })
  .strict();

/** Conteúdo exigido por canal (vale para criar e para o resultado de uma edição). */
export function validarConteudoCampanha(
  d: { canal?: CanalCampanha; mensagem?: string; emailAssunto?: string | null; emailCorpo?: string | null },
  ctx?: z.RefinementCtx,
): string | null {
  const canal = d.canal ?? 'WHATSAPP';
  let erro: { path: string; msg: string } | null = null;
  if (canal !== 'EMAIL' && !d.mensagem?.trim()) erro = { path: 'mensagem', msg: 'Escreva a mensagem do WhatsApp.' };
  else if (canal !== 'WHATSAPP' && !d.emailAssunto?.trim()) erro = { path: 'emailAssunto', msg: 'Informe o assunto do e-mail.' };
  else if (canal !== 'WHATSAPP' && !d.emailCorpo?.trim()) erro = { path: 'emailCorpo', msg: 'Escreva o texto do e-mail.' };
  if (erro && ctx) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [erro.path], message: erro.msg });
  return erro?.msg ?? null;
}

export const campanhaCreateSchema = campanhaBase.superRefine((d, ctx) => void validarConteudoCampanha(d, ctx));
export const campanhaUpdateSchema = campanhaBase.partial().strict();

export const enviadoSchema = z
  .object({
    mensagemFinal: z.string().max(1500).optional(),
    resultado: z.enum(['ENVIADO', 'INTERESSADO', 'NAO_INTERESSADO', 'RETORNAR']).optional(),
    followUpDias: z.number().int().min(0).max(60).optional(),
  })
  .strict();

export const abrirSchema = z
  .object({
    mensagem: z.string().trim().min(1).max(1500).optional(),
    /** WA_ME = link wa.me (padrão, celular/navegador); APP = whatsapp:// (app WhatsApp Desktop) */
    destino: z.enum(['WA_ME', 'APP']).default('WA_ME'),
  })
  .strict();

export const prepararLoteSchema = z.object({ tamanho: z.coerce.number().int().min(1).max(50).default(20) }).strict();

export const confirmarLoteSchema = z
  .object({
    enviados: z.array(z.string().uuid()).max(100).default([]),
    devolver: z.array(z.string().uuid()).max(100).default([]),
    followUpDias: z.number().int().min(0).max(60).optional(),
  })
  .strict()
  .refine((d) => d.enviados.length + d.devolver.length > 0, 'Selecione ao menos um cliente.')
  .refine((d) => !d.enviados.some((id) => d.devolver.includes(id)), 'Um cliente não pode ser enviado e devolvido ao mesmo tempo.');

export const pularSchema = z
  .object({ motivo: z.enum(['NUMERO_ERRADO', 'JA_FALEI_HOJE', 'OUTRO']), detalhe: z.string().max(300).optional() })
  .strict();

// ----------------------------------------------------------------------------- dashboard da equipe (specs/16)
export const PERIODOS_EQUIPE = ['hoje', 'ontem', '7d'] as const;
export const dashboardEquipeQuery = z.object({ periodo: z.enum(PERIODOS_EQUIPE).default('hoje') }).strict();
/** Contatos detalhados da equipe (specs/19). Booleans chegam como 'true'/'false' na query. */
const flag = z.enum(['true', 'false']).optional().transform((v) => v === 'true');
export const CONTATOS_EQUIPE_MAX = 1000;
export const contatosEquipeQuery = z
  .object({
    periodo: z.enum(PERIODOS_EQUIPE).default('hoje'),
    usuarioId: z.string().uuid().optional(),
    canal: z.enum(['WHATSAPP', 'LIGACAO', 'EMAIL', 'PRESENCIAL']).optional(),
    busca: z.string().trim().max(100).optional(),
    soVenda: flag,
    soStatus: flag,
    /** Esconde os envios de campanha (lote) e deixa só o trabalho manual */
    semLote: flag,
    formato: z.enum(['json', 'csv']).default('json'),
  })
  .strict();
/** Resumo por status da equipe (specs/20). */
export const statusEquipeQuery = z
  .object({ periodo: z.enum(PERIODOS_EQUIPE).default('hoje'), usuarioId: z.string().uuid().optional() })
  .strict();
export const STATUS_EQUIPE_MAX = 500;
export const statusClientesEquipeQuery = z
  .object({
    periodo: z.enum(PERIODOS_EQUIPE).default('hoje'),
    usuarioId: z.string().uuid().optional(),
    /** periodo = só quem entrou no status no período; todos = a carteira inteira do status */
    escopo: z.enum(['periodo', 'todos']).default('periodo'),
    busca: z.string().trim().max(100).optional(),
    /** Só quem veio deste status */
    origemId: z.string().uuid().optional(),
    formato: z.enum(['json', 'csv']).default('json'),
  })
  .strict();
export const metasSchema = z.object({ metaContatosDia: z.coerce.number().int().min(1).max(1000) }).strict();
/** Meta de um vendedor; null = volta a usar a meta da empresa */
export const metaVendedorSchema = z.object({ metaContatosDia: z.number().int().min(1).max(1000).nullable() }).strict();
